import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import {
  getProductGeorestrictions,
  deleteProductGeorestriction,
} from "@/api/productGeorestrictions.js";
import ProductGeorestrictionModal from "./ProductGeorestrictionModal.jsx";
import { US_STATE_NAMES, stateLabel } from "@/utils/usStates.js";

function matchWhere(match) {
  if (!match?.type) return "an unspecified area";
  switch (match.type) {
    case "state":
      return match.state ? stateLabel(match.state) : "a state";
    case "zip_codes": {
      const zips = Array.isArray(match.zip_codes) ? match.zip_codes : [];
      if (!zips.length) return "selected ZIP codes";
      if (zips.length === 1) return `ZIP ${zips[0]}`;
      if (zips.length <= 3) return `ZIPs ${zips.join(", ")}`;
      return `${zips.length} ZIP codes`;
    }
    case "state_city":
      return match.city && match.state
        ? `${match.city}, ${stateLabel(match.state)}`
        : "a city";
    case "state_county":
      return match.county && match.state
        ? `${match.county} County, ${stateLabel(match.state)}`
        : "a county";
    case "geojson":
      return "a custom GeoJSON area";
    default:
      return match.type;
  }
}

function ruleSentence(rule) {
  const action =
    rule.restrict === "pickup"
      ? "blocks pickup"
      : rule.restrict === "delivery"
        ? "blocks delivery"
        : "blocks pickup & delivery";

  const catCount = rule.target?.category_ids?.length || 0;
  const exceptCount = rule.target?.except_category_ids?.length || 0;
  const tagCount = rule.target?.tags?.length || 0;
  const targetType = rule.target?.type;
  const tagLabel = `${tagCount} tag${tagCount === 1 ? "" : "s"}`;
  let products = "products";
  if (targetType === "permissive_tags_any") {
    const scope = catCount
      ? `${catCount} categor${catCount === 1 ? "y" : "ies"}`
      : "any category";
    const except = exceptCount
      ? `, except ${exceptCount} categor${exceptCount === 1 ? "y" : "ies"}`
      : "";
    products = `products in ${scope}${except} that have none of ${tagLabel}`;
  } else if (targetType === "permissive_tags_all") {
    const scope = catCount
      ? `${catCount} categor${catCount === 1 ? "y" : "ies"}`
      : "any category";
    const except = exceptCount
      ? `, except ${exceptCount} categor${exceptCount === 1 ? "y" : "ies"}`
      : "";
    products = `products in ${scope}${except} missing any of ${tagLabel}`;
  } else if (targetType === "tags_all" && tagCount) {
    products = `all of ${tagLabel}`;
  } else if (targetType === "tags_any" && tagCount) {
    products = `any of ${tagLabel}`;
  } else {
    products = catCount
      ? `${catCount} categor${catCount === 1 ? "y" : "ies"}`
      : "categories";
    if (targetType === "categories_tags_all" && tagCount) {
      products += ` + all of ${tagCount} tag${tagCount === 1 ? "" : "s"}`;
    } else if (targetType === "categories_tags_any" && tagCount) {
      products += ` + any of ${tagCount} tag${tagCount === 1 ? "" : "s"}`;
    }
  }

  if (rule.address_type === "platform") {
    return `Platform-wide, ${action} for ${products}`;
  }

  const address =
    rule.address_type === "store"
      ? "store address"
      : rule.address_type === "either"
        ? "store or delivery address"
        : "delivery address";
  const where = matchWhere(rule.match);

  return `When ${address} is in ${where}, ${action} for ${products}`;
}

function Pill({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-300",
    rose: "bg-rose-100 text-rose-700 dark:bg-rose-400/10 dark:text-rose-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${tones[tone]}`}>
      {children}
    </span>
  );
}

function ProductGeorestrictionsTable() {
  const dispatch = useDispatch();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [stateFilter, setStateFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await getProductGeorestrictions();
      setRules(Array.isArray(data) ? data : []);
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to load product georestrictions",
        type: "error",
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleCreateNew = () => {
    setSelectedRule(null);
    setModalOpen(true);
  };

  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setModalOpen(true);
  };

  const handleDelete = async (rule) => {
    const label = rule.name || "this rule";
    if (!window.confirm(`Delete "${label}"?`)) return;
    try {
      setDeleting(rule.id);
      await deleteProductGeorestriction(rule.id);
      dispatch(addToast({ message: "Georestriction deleted", type: "success" }));
      loadRules();
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to delete georestriction",
        type: "error",
      }));
    } finally {
      setDeleting(null);
    }
  };

  const stateOptions = useMemo(() => {
    const countsByState = new Map();
    for (const rule of rules) {
      const code = (rule.match?.state || "").trim().toUpperCase();
      if (!code) continue;
      countsByState.set(code, (countsByState.get(code) || 0) + 1);
    }
    return [...countsByState.entries()]
      .map(([code, count]) => ({
        code,
        count,
        label: US_STATE_NAMES[code] ? `${US_STATE_NAMES[code]} (${code})` : code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rules]);

  const rulesInState = useMemo(() => {
    if (!stateFilter) return rules;
    return rules.filter(
      (rule) => (rule.match?.state || "").trim().toUpperCase() === stateFilter
    );
  }, [rules, stateFilter]);

  const counts = useMemo(() => {
    const active = rulesInState.filter((r) => r.active).length;
    return { all: rulesInState.length, active, inactive: rulesInState.length - active };
  }, [rulesInState]);

  const filteredRules = useMemo(() => {
    let list = rulesInState;
    if (statusFilter === "active") list = list.filter((r) => r.active);
    if (statusFilter === "inactive") list = list.filter((r) => !r.active);

    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter((r) => {
      const hay = [
        r.name,
        ruleSentence(r),
        r.address_type,
        r.restrict,
        r.match?.type,
        r.match?.state,
        r.match?.city,
        r.match?.county,
        r.target?.type,
        ...(Array.isArray(r.match?.zip_codes) ? r.match.zip_codes : []),
        ...(Array.isArray(r.target?.tags) ? r.target.tags : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rulesInState, searchQuery, statusFilter]);

  return (
    <div>
      <div className="mb-5">
        <p className="text-sm text-slate-600 dark:text-slate-400 max-w-3xl">
          Block pickup and/or delivery for products in certain categories (and optional tags)
          when a store or delivery address matches a geographic rule.
        </p>
      </div>

      <div className="sm:flex sm:justify-between sm:items-center mb-4 gap-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-1 min-w-0">
          <input
            type="text"
            placeholder="Search rules…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input w-full sm:w-72"
          />
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="form-select w-full sm:w-56"
            aria-label="Filter by state"
          >
            <option value="">All states</option>
            {stateOptions.map((state) => (
              <option key={state.code} value={state.code}>
                {state.label} ({state.count})
              </option>
            ))}
          </select>
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800/50">
            {[
              { id: "all", label: `All (${counts.all})` },
              { id: "active", label: `Active (${counts.active})` },
              { id: "inactive", label: `Inactive (${counts.inactive})` },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  statusFilter === f.id
                    ? "bg-white text-slate-800 shadow-sm dark:bg-slate-700 dark:text-slate-100"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <PrimaryButton onClick={handleCreateNew} additionalClasses="mt-3 sm:mt-0 shrink-0">
          Add Restriction
        </PrimaryButton>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Loading product georestrictions...
          </p>
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 px-6 py-14 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
            {rules.length === 0 ? "No restrictions yet" : "No matching restrictions"}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-md mx-auto">
            {rules.length === 0
              ? "Create a rule to block pickup or delivery for products when an address matches a state, city, ZIP, or custom area."
              : "Try a different search, state, or status filter."}
          </p>
          {rules.length === 0 ? (
            <PrimaryButton onClick={handleCreateNew}>Add Restriction</PrimaryButton>
          ) : (
            <SecondaryButton
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
                setStateFilter("");
              }}
            >
              Clear filters
            </SecondaryButton>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRules.map((rule) => (
            <div
              key={rule.id}
              className={`rounded-lg border bg-white dark:bg-theme-surface-accent px-4 py-3 sm:px-5 sm:py-4 ${
                rule.active
                  ? "border-slate-200 dark:border-slate-700"
                  : "border-slate-200/80 dark:border-slate-700/80 opacity-75"
              }`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {rule.name || "Untitled rule"}
                    </h3>
                    <Pill tone={rule.active ? "emerald" : "slate"}>
                      {rule.active ? "Active" : "Inactive"}
                    </Pill>
                  </div>

                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {ruleSentence(rule)}
                  </p>

                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    <Pill tone="indigo">
                      {rule.address_type === "platform"
                        ? "Platform wide"
                        : rule.address_type === "store"
                          ? "Store address"
                          : rule.address_type === "either"
                            ? "Store or delivery"
                            : "Delivery address"}
                    </Pill>
                    <Pill tone="rose">
                      {rule.restrict === "pickup"
                        ? "Block pickup"
                        : rule.restrict === "delivery"
                          ? "Block delivery"
                          : "Block both"}
                    </Pill>
                    {rule.match?.type === "geojson" ? (
                      <Pill tone="amber">Custom area</Pill>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0 sm:pl-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(rule)}
                    className="text-sm font-medium text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(rule)}
                    disabled={deleting === rule.id}
                    className="text-sm font-medium text-rose-500 hover:text-rose-600 dark:text-rose-400 dark:hover:text-rose-300"
                  >
                    {deleting === rule.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProductGeorestrictionModal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        rule={selectedRule}
        onSave={loadRules}
      />
    </div>
  );
}

export default ProductGeorestrictionsTable;
